import { LyfeKeyboardDatePicker } from "@lyfegen-platform/react-common";
import { defaultDateFormat, isDateValid, isEmptyValue, parseDate } from "@lyfegen-platform/utils";
import { MenuItem, TextField, TextFieldProps, useTheme } from "@material-ui/core";
import { format, isValid } from "date-fns";
import { useField, UseFieldProps } from "formik";
import { memo, useEffect } from "react";
import { Options } from "../models/common/options";
import getDatePattern from "../utils/date-pattern";
import useStyles from "./component-styles";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

const numericValueRegex = /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[0-9][0-9]*)$/;

interface IProps {
	cannotBeFuture?: boolean;
	cannotBePast?: boolean;
	maxDate?: string | Date;
	minDate?: string | Date;
	dateFormat?: string;
	options?: Array<Options>;
	height?: number;
	minValue?: number;
}

function LyfeFormField(props: TextFieldProps & IProps & UseFieldProps<any>) {
	const [field, input, helpers] = useField(props);
	const { cannotBeFuture, cannotBePast, maxDate, minDate, InputProps, type, ...other } = props;
	const { palette } = useTheme();
	const { inputLabelRoot } = useStyles({});
	const isError = (input.touched || props.disabled) && !!input.error;
	const dateFormat = props.dateFormat ? props.dateFormat : "yyyy-MM-dd";
	const defaultMaxDate = format(new Date(2100, 0, 1), dateFormat);
	const today = format(new Date(), dateFormat);
	const customHeight = props.height ? { height: props.height } : {};
	const inputDateFormat = getDatePattern();

	useEffect(() => {
		props.options &&
			props.options.length === 1 &&
			props.name &&
			props.options[0].value !== field.value &&
			helpers.setValue(props.options[0].value);
	}, [props.options, field.value, helpers]);

	const formatIfDate = (value: string | Date): string => {
		return typeof value === "string" ? format(parseDate(value), dateFormat) : format(value, dateFormat);
	};

	function getMaxDate() {
		if (cannotBeFuture) return formatIfDate(today);
		if (maxDate && isDateValid(maxDate)) return formatIfDate(maxDate);
		if (cannotBeFuture && maxDate && isDateValid(maxDate)) formatIfDate(maxDate);
		return defaultMaxDate;
	}

	function getMinDate() {
		if (cannotBePast) return formatIfDate(today);
		if (minDate && isDateValid(minDate)) return formatIfDate(minDate);
		if (cannotBePast && minDate && isDateValid(minDate)) return formatIfDate(minDate);
		return undefined;
	}

	function getMinValue() {
		if (props.type === "number") {
			return 0;
		}
		if (props.type === "date") {
			return getMinDate();
		}
		return undefined;
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (props.type === "number" && ["-", "e", "E"].includes(e.key)) {
			e.preventDefault();
		}
	}

	function resetField() {
		field.onChange("TEMP_RESET");
		setTimeout(() => {
			field.onChange("");
			helpers.setValue("");
		}, 0);
	}

	function onChange(date: MaterialUiPickersDate, value?: string | null | undefined) {
		if (date && isValid(date)) {
			field.onChange(format(date, defaultDateFormat));
		} else if (value && !value.includes("_")) {
			resetField();
		} else if (isEmptyValue(date)) {
			field.onChange(null);
		}
		helpers.setTouched(true);
	}

	if (props.type !== "date") {
		return (
			<TextField
				error={isError}
				helperText={isError ? input.error : null}
				required={props.required && !props.disabled}
				InputProps={{
					...InputProps,
					inputProps: {
						...InputProps?.inputProps,
						min: props.minValue || getMinValue(),
						style: { backgroundColor: palette.background.paper, ...customHeight },
					},
				}}
				SelectProps={{
					MenuProps: {
						anchorOrigin: {
							vertical: "bottom",
							horizontal: "left",
						},
						getContentAnchorEl: null,
						transitionDuration: 0,
					},
				}}
				variant="outlined"
				onKeyDown={handleKeyDown}
				{...field}
				onChange={(e: any) => {
					const { value } = e.target;
					if (props.type === "number" && value.toString().trim() && !numericValueRegex.test(value.toString())) {
						e.preventDefault();
						return;
					}

					field.onChange(e);
				}}
				type={type as any}
				{...other}
			>
				{props.options &&
					props.options.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
			</TextField>
		);
	}

	return (
		<LyfeKeyboardDatePicker
			autoOk
			error={isError}
			helperText={isError ? input.error : null}
			required={props.required && !props.disabled}
			disabled={props.disabled}
			variant="inline"
			id={other.id}
			inputVariant="outlined"
			label={other.label}
			maxDate={props.type === "date" ? getMaxDate() : undefined}
			minDate={getMinValue()}
			InputLabelProps={{
				shrink: true,
				classes: { root: inputLabelRoot },
			}}
			InputProps={{
				...InputProps,
			}}
			format={inputDateFormat}
			placeholder={inputDateFormat.toLowerCase()}
			onKeyDown={handleKeyDown}
			{...field}
			defaultValue={null}
			value={field.value || null}
			onChange={onChange}
			fullWidth
		>
			{props.children}
		</LyfeKeyboardDatePicker>
	);
}

export default memo(LyfeFormField);
